import {
  platforms_ebay,
  platforms_ebay_alias,
  platforms_ebay_fetch_estimate,
  platforms_ebay_messaging,
  platforms_ebay_messaging_orders,
  platforms_ebay_order_sync,
  platforms_ebay_policies,
  platforms_ebay_oauth_url,
  VITE_API_URL,
} from "@/constants";
import type { AuthFetchParams } from "./global";
import { queryOptions } from "@tanstack/react-query";
import type {
  EbayAccount,
  EbayMessageConfig,
  EbayOrderSyncConfig,
  EbayOrderMessageListItem,
  EbayPolicies,
} from "@/lib/types";

export interface EbayAccountMin {
  id: string;
  alias: string;
  username: string;
  ebay_id: string;
  marketplace_id: string;
  refresh_token_expiry: Date;
  platform_connection_id: string;
  return_policy_id: string | null;
  return_policy_name: string | null;
  payment_policy_id: string | null;
  payment_policy_name: string | null;
  fulfillment_policy_id: string | null;
  fulfillment_policy_name: string | null;
}

export interface EbayFetchEstimate {
  total_listings: number;
  total_pages: number;
  page_size: number;
}

export async function getConnectUrl(params: AuthFetchParams) {
  const res = await fetch(`${VITE_API_URL}${platforms_ebay_oauth_url}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${params.token}`,
      "X-Inventoree-Organization": params.orgId,
    },
    credentials: "include",
  });

  const access = res.headers.get("access-token");
  if (access) params.setToken(access);

  return (await res.json()) as { url: string };
}

export async function createConnectUrl(params: AuthFetchParams) {
  const res = await fetch(`${VITE_API_URL}${platforms_ebay_oauth_url}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${params.token}`,
      "X-Inventoree-Organization": params.orgId,
    },
    credentials: "include",
  });

  const access = res.headers.get("access-token");
  if (access) params.setToken(access);

  return (await res.json()) as { url: string };
}

export async function getAccount(alias: string, params: AuthFetchParams) {
  const resp = await fetch(
    `${VITE_API_URL}${platforms_ebay_alias}/${encodeURIComponent(alias)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.token}`,
        "X-Inventoree-Organization": params.orgId,
      },
      credentials: "include",
    },
  );

  const access = resp.headers.get("access-token");
  if (access) params.setToken(access);

  if (!resp.ok) {
    throw new Error("Failed to load ebay account");
  }

  const res = (await resp.json()) as EbayAccount;

  return {
    id: res.id,
    alias: res.alias,
    username: res.username,
    ebay_id: res.ebay_id,
    marketplace_id: res.marketplace_id,
    refresh_token_expiry: res.refresh_token_expiry,
    platform_connection_id: res.platform_connection_id,
    return_policy_id: res.return_policy_id ?? null,
    return_policy_name: res.return_policy_name ?? null,
    payment_policy_id: res.payment_policy_id ?? null,
    payment_policy_name: res.payment_policy_name ?? null,
    fulfillment_policy_id: res.fulfillment_policy_id ?? null,
    fulfillment_policy_name: res.fulfillment_policy_name ?? null,
  } as EbayAccountMin;
}

export async function getEbayPolicies(
  alias: string,
  params: AuthFetchParams,
) {
  const resp = await fetch(
    `${VITE_API_URL}${platforms_ebay_policies}/${encodeURIComponent(alias)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.token}`,
        "X-Inventoree-Organization": params.orgId,
      },
      credentials: "include",
    },
  );

  const access = resp.headers.get("access-token");
  if (access) params.setToken(access);

  if (!resp.ok) {
    throw new Error("Failed to load ebay policies");
  }

  return (await resp.json()) as EbayPolicies;
}

export async function getAccounts(params: AuthFetchParams) {
  const resp = await fetch(`${VITE_API_URL}${platforms_ebay}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${params.token}`,
      "X-Inventoree-Organization": params.orgId,
    },
    credentials: "include",
  });

  const access = resp.headers.get("access-token");
  if (access) params.setToken(access);

  return (await resp.json()) as EbayAccountMin[];
}

export async function getEbayFetchEstimate(
  accountId: string,
  params: AuthFetchParams,
) {
  const resp = await fetch(
    `${VITE_API_URL}${platforms_ebay_fetch_estimate}/${accountId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.token}`,
        "X-Inventoree-Organization": params.orgId,
      },
      credentials: "include",
    },
  );

  const access = resp.headers.get("access-token");
  if (access) params.setToken(access);

  if (!resp.ok) {
    throw new Error(await resp.text());
  }

  return (await resp.json()) as EbayFetchEstimate;
}

export async function getEbayMessageConfig(
  pcId: string,
  params: AuthFetchParams,
) {
  const resp = await fetch(
    `${VITE_API_URL}${platforms_ebay_messaging}/${pcId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.token}`,
        "X-Inventoree-Organization": params.orgId,
      },
      credentials: "include",
    },
  );

  const access = resp.headers.get("access-token");
  if (access) params.setToken(access);

  return (await resp.json()) as EbayMessageConfig;
}

export async function getEbayOrderMessages(
  pcId: string,
  params: AuthFetchParams,
) {
  const resp = await fetch(
    `${VITE_API_URL}${platforms_ebay_messaging_orders}/${pcId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.token}`,
        "X-Inventoree-Organization": params.orgId,
      },
      credentials: "include",
    },
  );

  const access = resp.headers.get("access-token");
  if (access) params.setToken(access);

  return (await resp.json()) as EbayOrderMessageListItem[];
}

export async function getEbayOrderSyncConfig(
  pcId: string,
  params: AuthFetchParams,
) {
  const resp = await fetch(
    `${VITE_API_URL}${platforms_ebay_order_sync}/${pcId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${params.token}`,
        "X-Inventoree-Organization": params.orgId,
      },
      credentials: "include",
    },
  );

  const access = resp.headers.get("access-token");
  if (access) params.setToken(access);

  return (await resp.json()) as EbayOrderSyncConfig;
}

export const ebayAccountsQueryOpts = (params: AuthFetchParams) =>
  queryOptions({
    queryKey: ["ebayAccounts"],
    queryFn: async () => await getAccounts(params),
  });

export const accountQueryOpts = (alias: string, params: AuthFetchParams) =>
  queryOptions({
    queryKey: ["ebayAccount", alias],
    queryFn: async () => await getAccount(alias, params),
  });

export const ebayPoliciesQueryOpts = (
  alias: string,
  params: AuthFetchParams,
) =>
  queryOptions({
    queryKey: ["ebayPolicies", alias],
    queryFn: async () => await getEbayPolicies(alias, params),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

export const ebayMessageConfigQueryOpts = (
  pcId: string,
  params: AuthFetchParams,
) =>
  queryOptions({
    queryKey: ["ebayMessageConfig", pcId],
    queryFn: async () => await getEbayMessageConfig(pcId, params),
  });

export const ebayOrderMessagesQueryOpts = (
  pcId: string,
  params: AuthFetchParams,
) =>
  queryOptions({
    queryKey: ["ebayOrderMessages", pcId],
    queryFn: async () => await getEbayOrderMessages(pcId, params),
  });

export const ebayOrderSyncConfigQueryOpts = (
  pcId: string,
  params: AuthFetchParams,
) =>
  queryOptions({
    queryKey: ["ebayOrderSyncConfig", pcId],
    queryFn: async () => await getEbayOrderSyncConfig(pcId, params),
  });
